import {useCallback, useState} from 'react';
import type {FunctionComponent, JSX} from 'react';
import {Button} from '@tempo/core';

// Styled
import {
	LicenseCardContent,
	LicenseCardLink,
	LicenseCardRoot,
	LicenseCardSubtitle,
	LicenseCardText,
	LicenseCardTitle,
	LicenseCardToggle,
} from './styled';

// Types
import type LicensesManifest from '@public/licenses-manifest.json';

// Props
type LicenseCardProps = {
	module: string;
	info: (typeof LicensesManifest)[keyof typeof LicensesManifest];
};

export const LicenseCard: FunctionComponent<LicenseCardProps> = ({
	module,
	info,
}) => {
	// States
	const [isToggled, setToggle] = useState(false);

	// Handlers
	const handleToggleMore = useCallback(() => {
		setToggle((isToggled) => !isToggled);
	}, []);

	let licenses: string | JSX.Element = info.licenses;
	const [, name, version] = module.split(/^(.+)@(\d.*)$/);
	const heading = `${name} (${version})`;

	// Clean `(MIT OR Apache 2.0 OR GPL)` to `MIT, Apache 2.0, GPL`
	if (licenses.startsWith('(')) {
		licenses = licenses
			.replace(/^\((.+)\)$/, '$1')
			.split('OR')
			.map((license) => license.trim())
			.join(', ');
	}

	// Make custom licenses clickable (`Custom: https://…`)
	if (licenses.startsWith('Custom: ')) {
		const url = licenses.replace('Custom: ', '');
		licenses = (
			<>
				Custom: <LicenseCardLink href={url}>{url}</LicenseCardLink>
			</>
		);
	}

	return (
		<LicenseCardRoot>
			<LicenseCardTitle>
				{'repository' in info ? (
					<LicenseCardLink href={info.repository}>{heading}</LicenseCardLink>
				) : (
					heading
				)}
			</LicenseCardTitle>
			<LicenseCardSubtitle>Declared License(s)</LicenseCardSubtitle>
			<p>{licenses}</p>
			{info.licenseText && (
				<LicenseCardContent maxHeight={!isToggled ? '120px' : 'initial'}>
					<LicenseCardToggle>
						<Button size="small" onClick={handleToggleMore}>
							{!isToggled ? 'show more' : 'show less'}
						</Button>
					</LicenseCardToggle>
					<LicenseCardText>{info.licenseText}</LicenseCardText>
				</LicenseCardContent>
			)}
		</LicenseCardRoot>
	);
};
